/**
 * The menu as a tree (WAI-ARIA tree pattern) — classic's sidebar, and any
 * layout that needs the whole menu at once (a drawer, an "All screens" panel).
 * Any depth; folders toggle, screens open. Arrow keys move, Right/Left expand
 * and collapse (or step into / out of a folder), Home/End jump, Enter/Space
 * activate. Right-click / the context-menu key offers Pin.
 */
import { ChevronDown, ChevronRight, Star } from 'lucide-react'
import { useRef, type KeyboardEvent } from 'react'
import { tw } from '../../../testHooks'
import { NavIcon } from '../navIcons'
import { S } from '../tokens'
import type { NavTreeNode } from '../tree'
import type { ShellNavModel } from '../useShellNav'
import { useEntryMenu } from './ShellLists'

export interface MenuTreeProps {
  model: ShellNavModel
  /** Roots to draw (default: the whole menu). */
  roots?: NavTreeNode[]
  /** Called after a SCREEN is opened (close a drawer). */
  onPicked?: () => void
  label?: string
  /** Show the star on pinned entries. */
  showPins?: boolean
}

export function MenuTree({ model, roots, onPicked, label = 'Navigation', showPins = true }: MenuTreeProps) {
  const ref = useRef<HTMLDivElement>(null)
  const entryMenu = useEntryMenu(model)
  const nodes = roots ?? model.roots
  const selectedId = model.current?.id || null
  // Roving tabindex: the selected entry, else the first.
  const focusId = selectedId && model.tree.byId.has(selectedId) && isVisible(model, selectedId)
    ? selectedId : nodes[0]?.item.id

  const items = () => [...(ref.current?.querySelectorAll<HTMLElement>('[role="treeitem"]') || [])]
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const els = items()
    const cur = document.activeElement as HTMLElement
    const i = els.indexOf(cur)
    if (i < 0) return
    const id = cur.dataset.nodeId || ''
    const node = model.tree.byId.get(id)
    const move = (j: number) => { e.preventDefault(); els[Math.max(0, Math.min(els.length - 1, j))]?.focus() }
    switch (e.key) {
      case 'ArrowDown': move(i + 1); break
      case 'ArrowUp': move(i - 1); break
      case 'Home': move(0); break
      case 'End': move(els.length - 1); break
      case 'ArrowRight':
        if (node?.isContainer && node.children.length) {
          e.preventDefault()
          if (!model.expanded.has(id)) model.toggleExpanded(id)
          else move(i + 1)
        }
        break
      case 'ArrowLeft': {
        e.preventDefault()
        if (node?.isContainer && model.expanded.has(id)) { model.toggleExpanded(id); break }
        const parentId = node?.path[node.path.length - 1]?.id
        const parent = parentId ? els.find((x) => x.dataset.nodeId === parentId) : undefined
        parent?.focus()
        break
      }
      default:
    }
  }

  return (
    <div ref={ref} role="tree" aria-label={label} onKeyDown={onKey}>
      {nodes.map((n) => (
        <TreeRow key={n.item.id} node={n} model={model} focusId={focusId} onPicked={onPicked}
          showPins={showPins} entryMenu={entryMenu} />
      ))}
      {entryMenu.ui}
    </div>
  )
}

function isVisible(model: ShellNavModel, id: string): boolean {
  const n = model.tree.byId.get(id)
  return !!n && n.path.every((p) => model.expanded.has(p.id))
}

function TreeRow({ node, model, focusId, onPicked, showPins, entryMenu }: {
  node: NavTreeNode
  model: ShellNavModel
  focusId?: string
  onPicked?: () => void
  showPins: boolean
  entryMenu: ReturnType<typeof useEntryMenu>
}) {
  const { item, isFolder, isContainer, children, depth } = node
  const expanded = model.expanded.has(item.id)
  const selected = model.current?.id === item.id
  const pinned = showPins && !isFolder && model.isPinned(item)
  const activate = () => {
    if (isFolder) { model.toggleExpanded(item.id); return }
    model.open(item)
    onPicked?.()
  }
  return (
    <div role="none">
      <button
        type="button"
        role="treeitem"
        data-node-id={item.id}
        aria-level={depth + 1}
        aria-expanded={isContainer && children.length ? expanded : undefined}
        aria-selected={selected}
        aria-current={selected ? 'page' : undefined}
        tabIndex={item.id === focusId ? 0 : -1}
        className="tw-sh-item"
        {...tw('nav', item.obj_name || item.title, { type: item.obj_type, folder: isFolder, selected })}
        onClick={activate}
        onContextMenu={entryMenu.onContextMenu(item)}
        onKeyDown={entryMenu.onKeyDown(item)}
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', gap: 7, width: '100%',
          minHeight: 38, padding: '6px 8px', paddingLeft: `${0.5 + depth * 1.1}rem`,
          borderRadius: S.radius, border: 'none', cursor: 'pointer', fontFamily: 'inherit',
          background: selected ? S.surface2 : 'transparent',
          color: selected ? S.text : S.text2,
          fontSize: 14.5, fontWeight: selected ? 600 : (depth === 0 ? 500 : 400), textAlign: 'left',
        }}
      >
        <span aria-hidden style={{
          position: 'absolute', left: 0, top: 6, bottom: 6, width: 3, borderRadius: 3,
          background: selected ? S.accent : 'transparent',
        }} />
        <span aria-hidden style={{ width: 14, flexShrink: 0, display: 'inline-flex', color: S.faint }}
          onClick={(e) => {
            // A website (X) opens on click; its chevron alone toggles its pages.
            if (!isFolder && isContainer) { e.stopPropagation(); model.toggleExpanded(item.id) }
          }}>
          {isContainer && children.length ? (expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
        </span>
        <NavIcon node={item} size={16} color={selected ? S.accent : undefined} />
        <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0 }}>{item.title}</span>
        {pinned && <Star size={13} aria-label="Pinned" role="img" fill={S.star} color={S.starStroke} />}
      </button>
      {isContainer && expanded && children.length > 0 && (
        <div role="group">
          {children.map((c) => (
            <TreeRow key={c.item.id} node={c} model={model} focusId={focusId} onPicked={onPicked}
              showPins={showPins} entryMenu={entryMenu} />
          ))}
        </div>
      )}
      {isFolder && expanded && children.length === 0 && (
        <div style={{ padding: `4px 10px 8px ${1.5 + (depth + 1) * 1.1}rem`, fontSize: 13.5, color: S.text2 }}>
          No screens yet
        </div>
      )}
    </div>
  )
}
